import React from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";

import ngoPat1 from "../../../assets/Images/NGO_Pat1.png";
import ngoPat2 from "../../../assets/Images/NGO_Pat2.png";
import ngoPat3 from "../../../assets/Images/NGO_Pat3.png";
import ngoPat4 from "../../../assets/Images/NGO_Pat4.png";
import greenBgTick from "../../../assets/Icons/GreenBG_Tick.svg";

const fallbackPartners = [
  {
    id: 1,
    logo: ngoPat1,
    name: "Bal Sahara Trust",
    category: "Children & Orphanage",
    description: "Empowering orphaned and underprivileged children through essential care.",
    received: "120+ Products Received",
  },
  {
    id: 2,
    logo: ngoPat2,
    name: "Apna Ghar Shelter",
    category: "Women's Shelter Home",
    description: "Providing a safe haven and essential support for women seeking protection.",
    received: "85+ Products Received",
  },
  {
    id: 3,
    logo: ngoPat3,
    name: "Jeevan Jyoti Old Age",
    category: "Senior Citizens Care",
    description: "Offering dedicated companionship and specialized healthcare to ensure dignity.",
    received: "200+ Products Received",
  },
  {
    id: 4,
    logo: ngoPat4,
    name: "Shiksha Foundation",
    category: "Tribal Education",
    description: "Bridging the educational gap for indigenous communities through specialized learning.",
    received: "95+ Products Received",
  },
];

const NgoPartnersSection = () => {
  const theme = useTheme();
  const home = useSelector((state) => state.cms.home);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDesktop = useMediaQuery("(min-width: 1400px)");

  const sections = Array.isArray(home?.data) ? home.data : [];
  const ngoPartnersCms = sections.find((item) => {
    if (!item) return false;
    const isActive = item.status === 1 || item.status === true;
    if (!isActive) return false;
    return String(item.title || "").toLowerCase().includes("trusted ngos who receive your purchased products");
  });

  const sectionTitle = ngoPartnersCms?.title || "Trusted NGOs Who Receive Your Purchased Products";
  const sectionDescription =
    ngoPartnersCms?.description ||
    "Every product you purchase from a vendor goes directly to one of our verified NGO partners. Full transparency guaranteed.";

  const cmsPartners = Array.isArray(ngoPartnersCms?.relatedCMS)
    ? ngoPartnersCms.relatedCMS
        .filter((item) => item && (item.status === 1 || item.status === true))
        .map((item) => ({
          id: item.id,
          logo: item.image || ngoPat1,
          name: item.title || "",
          category: item.stand_title || "",
          description: item.description || "",
          received: item.sub_title || "",
        }))
    : [];

  const partners = cmsPartners.length > 0 ? cmsPartners : fallbackPartners;

  return (
    <Box
      sx={{
        width: isMobile ? "100%" : "94.44%",
        maxWidth: "1440px",
        mx: "auto",
        px: isMobile ? "16px" : 0,
        mb: isMobile ? "30px" : "60px",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: isMobile ? "10px" : "20px", mb: isMobile ? "20px" : "30px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? "13px" : "12px" }}>
          <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#A9C74E" }} />
          <Typography sx={{ fontWeight: 400, fontSize: isMobile ? "10px" : "14px", lineHeight: isMobile ? 1 : "18.2px", letterSpacing: isMobile ? "2px" : "2.1px", textTransform: "uppercase", color: "#131313" }}>
            Our NGO Partners
          </Typography>
        </Box>

        <Typography
          sx={{
            fontWeight: 700,
            fontSize: isMobile ? "18px" : "36px",
            lineHeight: isMobile ? "29.124px" : "1.4",
            letterSpacing: "-0.36px",
            color: "#1D1D1B",
            textAlign: "center",
            maxWidth: isMobile ? "328px" : "894px",
          }}
        >
          {sectionTitle}
        </Typography>

        <Typography
          sx={{
            fontWeight: 400,
            fontSize: isMobile ? "12px" : "16px",
            lineHeight: isMobile ? "19.416px" : "25.888px",
            letterSpacing: isMobile ? "-0.24px" : "normal",
            color: isMobile ? "#7E7878" : "#7D7D7D",
            textAlign: "center",
            maxWidth: isMobile ? "314px" : "920px",
          }}
        >
          {sectionDescription}
        </Typography>
      </Box>

      <Box
        className="hide-scrollbar"
        sx={{
          display: isDesktop ? "grid" : "flex",
          gridTemplateColumns: isDesktop ? "repeat(4, 325px)" : "none",
          justifyContent: isDesktop ? "space-between" : "flex-start",
          alignItems: isMobile ? "flex-end" : "stretch",
          gap: isMobile ? "12px" : "20px",
          overflowX: isDesktop ? "visible" : "auto",
          overflowY: isDesktop ? "visible" : "hidden",
          py: isDesktop ? "4px" : 0,
          pb: isDesktop ? 0 : "4px",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        {partners.map((partner) => (
          <Box
            key={partner.id}
            sx={{
              width: isDesktop ? "325px" : isMobile ? "227.936px" : "300px",
              minWidth: isDesktop ? "325px" : isMobile ? "227.936px" : "300px",
              borderRadius: isMobile ? "14px" : "20px",
              background: "#FFFFFF",
              boxShadow: isMobile ? "0px 2.78px 17.68px rgba(0, 0, 0, 0.06)" : "0px 4px 24px 0px rgba(0, 0, 0, 0.06)",
              border: "1px solid rgba(15, 23, 42, 0.03)",
              px: isMobile ? "14px" : "20px",
              py: isMobile ? "16px" : "30px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: isMobile ? "16.678px" : "24px",
            }}
          >
            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: isMobile ? "16.678px" : "24px" }}>
              <Box sx={{ height: isMobile ? "61.154px" : "88px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Box component="img" src={partner.logo} alt={partner.name} sx={{ maxHeight: isMobile ? "61.154px" : "88px", maxWidth: isMobile ? "169.136px" : "244px", width: "auto", height: "auto", objectFit: "contain" }} />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: isMobile ? "12.509px" : "18px", width: "100%", minWidth: 0 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", width: "100%", minWidth: 0 }}>
                  <Typography
                    title={partner.name}
                    sx={{
                      fontWeight: 700,
                      fontSize: isMobile ? "14px" : "18px",
                      lineHeight: isMobile ? "17.924px" : "25.793px",
                      color: "#0F172A",
                      textAlign: "center",
                      width: "100%",
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {partner.name}
                  </Typography>
                  <Typography sx={{ fontWeight: 500, fontSize: isMobile ? "10px" : "12px", lineHeight: 1, color: "#8EA946", textAlign: "center" }}>
                    {partner.category}
                  </Typography>
                </Box>
                <Typography
                  title={partner.description}
                  sx={{
                    fontWeight: 400,
                    fontSize: isMobile ? "12px" : "14px",
                    lineHeight: isMobile ? "18.3px" : "21px",
                    color: "#4A4A49",
                    textAlign: "center",
                    width: "100%",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    wordBreak: "break-word",
                  }}
                >
                  {partner.description}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                borderRadius: "34.746px",
                background: "#F5F8ED",
                px: isMobile ? "15.288px" : "22px",
                py: isMobile ? "8.339px" : "12px",
                display: "flex",
                alignItems: "center",
                gap: isMobile ? "2.78px" : "4px",
              }}
            >
              <Box component="img" src={greenBgTick} alt="received" sx={{ width: isMobile ? "16px" : "22px", height: isMobile ? "16px" : "22px" }} />
              <Typography sx={{ fontWeight: 500, fontSize: isMobile ? "12px" : "14px", lineHeight: isMobile ? "12.803px" : "18.424px", color: "#6F8437", whiteSpace: "nowrap" }}>
                {partner.received}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default NgoPartnersSection;
